import { Head, router } from '@inertiajs/react';
import { useState } from 'react';
import { MainLayout } from '../../layouts/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import { route } from '../../utils/route';

export default function RecipeSubmit() {
  const [name, setName] = useState('');
  const [steps, setSteps] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, image: 'La imagen no puede exceder 2MB.' }));
        return;
      }
      setImage(file);
      setErrors((prev) => {
        const { image: _, ...rest } = prev;
        return rest;
      });
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    const formData = new FormData();
    formData.append('name', name);
    formData.append('steps', steps);
    if (image) {
      formData.append('image', image);
    }

    router.post(route('recipes.store'), formData, {
      forceFormData: true,
      onSuccess: () => {
        setSuccess(true);
        setName('');
        setSteps('');
        setImage(null);
        setImagePreview(null);
      },
      onError: (errs) => {
        setErrors(errs);
      },
      onFinish: () => {
        setLoading(false);
      },
    });
  };

  if (success) {
    return (
      <MainLayout>
        <Head>
          <title>Receta enviada | Abasto Virtual</title>
          <meta name="robots" content="noindex" />
        </Head>
        <div className="mx-auto max-w-2xl px-4 py-12">
          <div className="rounded-lg border border-green-200 bg-green-50 p-8 text-center">
            <div className="mb-4 text-5xl text-green-600">
              <i className="fa fa-check-circle" />
            </div>
            <h1 className="mb-2 text-2xl font-bold text-gray-900">¡Gracias por tu receta!</h1>
            <p className="text-gray-700">
              Tu receta fue enviada correctamente y está pendiente de revisión. La vamos a revisar y publicar pronto.
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Head>
        <title>Enviá tu receta | Abasto Virtual</title>
        <meta name="robots" content="noindex" />
      </Head>

      <div className="mx-auto max-w-2xl px-4 py-6">
        <PageHeader
          title="Enviá tu receta"
          subtitle="Compartí tu receta favorita con la comunidad de Abasto Virtual."
        />

        <form
          onSubmit={handleSubmit}
          className="mt-6 space-y-6 rounded-lg border border-gray-200 bg-white p-6"
          encType="multipart/form-data"
        >
          {errors.general && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{errors.general}</div>
          )}

          <div>
            <label htmlFor="name" className="mb-1 block text-sm font-medium text-gray-700">
              Nombre de la receta
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
              placeholder="Ej: Milanesas con puré"
              required
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="steps" className="mb-1 block text-sm font-medium text-gray-700">
              Descripción / Pasos
            </label>
            <textarea
              id="steps"
              value={steps}
              onChange={(e) => setSteps(e.target.value)}
              rows={8}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
              placeholder={"## Preparación\\n\\n1. Lavá las verduras...\\n2. Cortá en cubos...\\n\\n**Tip:** Usá un cuchillo bien afilado."}
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Acepta formato Markdown: **negrita**, listas, encabezados ##, etc.
            </p>
            {errors.steps && <p className="mt-1 text-sm text-red-600">{errors.steps}</p>}
          </div>

          <div>
            <label htmlFor="image" className="mb-1 block text-sm font-medium text-gray-700">
              Imagen de la receta
            </label>
            <input
              id="image"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-green-600 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-green-700"
              required
            />
            <p className="mt-1 text-xs text-gray-500">JPG, PNG o WEBP. Máximo 2MB.</p>
            {errors.image && <p className="mt-1 text-sm text-red-600">{errors.image}</p>}

            {imagePreview && (
              <div className="mt-3">
                <img
                  src={imagePreview}
                  alt="Vista previa"
                  className="h-48 w-full rounded-lg object-cover"
                />
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 disabled:opacity-60 md:w-auto"
          >
            {loading ? (
              <>
                <i className="fa fa-spinner fa-spin" />
                Enviando...
              </>
            ) : (
              <>
                <i className="fa fa-paper-plane" />
                Enviar receta
              </>
            )}
          </button>
        </form>
      </div>
    </MainLayout>
  );
}
